import {
  FormControl,
  Input,
  Flex,
  IconButton,
  Box,
  FormErrorMessage,
} from '@chakra-ui/react';
import { CustomBinIcon } from '../../../theme/multicolor-icons/CustomBinIcon';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import {
  setAppleAppStoreUrl,
  addAppleAppStoreUrl,
  removeAppleAppStoreUrl,
} from '../../../store/redux/slices/template.slice';
import { LabelWithTooltip } from './LabelWithTooltip';
import { useTemplateFieldValidation } from './validation/useTemplateFieldValidation';
import AddMoreButton from './AddMoreButton';
import { useRef, useEffect, RefObject } from 'react';
import FieldEditIndicator from './FieldEditIndicator';
import { useFieldEditIndicator } from './hooks/useFieldEditIndicator';
import { CustomErrorIcon } from '../../../theme/icons/CustomErrorIcon';

const AppleAppLinks = () => {
  const { appleAppStoreUrl } = useAppSelector((s) => s.template.appLinking);
  const dispatch = useAppDispatch();
  const linksLimit = 10;
  const limitReached = appleAppStoreUrl.length >= linksLimit;
  const { validateField, handleFocus, handleBlur } =
    useTemplateFieldValidation('appLinking');
  const errors = useAppSelector((s) => s.templateErrors);
  const appleAppStoreUrlRefs = useRef<(HTMLInputElement | null)[]>([]);
  const wrapperRefs = useRef<Map<number, RefObject<HTMLDivElement>>>(new Map());

  useEffect(() => {
    appleAppStoreUrlRefs.current = appleAppStoreUrlRefs.current.slice(
      0,
      appleAppStoreUrl.length
    );
    appleAppStoreUrl.forEach((_, i) => {
      if (!wrapperRefs.current.has(i)) {
        wrapperRefs.current.set(i, {
          current: null,
        } as RefObject<HTMLDivElement>);
      }
    });
    Array.from(wrapperRefs.current.keys()).forEach((key) => {
      if (key >= appleAppStoreUrl.length) {
        wrapperRefs.current.delete(key);
      }
    });
  }, [appleAppStoreUrl.length]);

  const { activeFieldRef, isFieldBeingEdited, activeIndex } =
    useFieldEditIndicator({
      arrayFieldPattern: 'appLinking.appleAppStoreUrl.',
      getRefFromIndex: (index) => wrapperRefs.current.get(index),
    });

  return (
    <FormControl isInvalid={!!errors['appLinking.appleAppStoreUrl']}>
      <LabelWithTooltip
        label="Apple App Store URLs"
        tooltip="Add one or more valid App Store links (max 10)."
      />

      {appleAppStoreUrl.map((url, i) => {
        const wrapperRef =
          wrapperRefs.current.get(i) ||
          ({ current: null } as RefObject<HTMLDivElement>);
        return (
          <FieldEditIndicator
            key={i}
            fieldRef={wrapperRef}
            isFieldBeingEdited={isFieldBeingEdited}
            activeFieldRef={activeFieldRef}
            left="-40px"
            top="25px"
            show={isFieldBeingEdited && activeIndex === i}
          >
            <Flex align="center" mb={3}>
              <Input
                ref={(el) => {
                  appleAppStoreUrlRefs.current[i] = el;
                }}
                value={url}
                placeholder="https://apps.apple.com/..."
                onChange={(e) => {
                  const value = e.target.value;
                  const next = [...appleAppStoreUrl];
                  next[i] = value;
                  dispatch(setAppleAppStoreUrl({ index: i, value }));
                  validateField('appleAppStoreUrl', next, {
                    appleAppStoreUrl: next,
                  });
                }}
                onFocus={() => handleFocus(`appLinking.appleAppStoreUrl.${i}`)}
                onBlur={() => handleBlur()}
              />
              <IconButton
                ml={2}
                aria-label="Remove link"
                icon={<CustomBinIcon />}
                isDisabled={appleAppStoreUrl.length <= 1 && !url}
                variant="ghost"
                onClick={() => {
                  if (appleAppStoreUrl.length <= 1) {
                    dispatch(setAppleAppStoreUrl({ index: i, value: '' }));
                    validateField('appleAppStoreUrl', [''], {
                      appleAppStoreUrl: [''],
                    });
                  } else {
                    const next = appleAppStoreUrl.filter((_, idx) => idx !== i);
                    dispatch(removeAppleAppStoreUrl(i));
                    validateField('appleAppStoreUrl', next, {
                      appleAppStoreUrl: next,
                    });
                  }
                }}
              />
            </Flex>
          </FieldEditIndicator>
        );
      })}

      {errors['appLinking.appleAppStoreUrl'] && (
        <FormErrorMessage pl={2} display="flex" alignItems="center" gap={1.5}>
          <CustomErrorIcon />
          {errors['appLinking.appleAppStoreUrl']}
        </FormErrorMessage>
      )}

      {!limitReached && (
        <Box mt={4}>
          <AddMoreButton
            text="Add another URL"
            onClick={() => dispatch(addAppleAppStoreUrl())}
          />
        </Box>
      )}
    </FormControl>
  );
};

export default AppleAppLinks;
